import { useEffect, useRef, useState } from 'react'

/**
 * Compact discovery filter bar: presets stay visible; per-filter checkboxes live in a dropdown.
 */
export default function DiscoveryFilterToolbar({
  filters,
  onChange,
  defaultFilters,
  items,
  summary,
  hideNonEnglish,
  onHideNonEnglishChange,
  hideNonEnglishTitle = 'Hide rows with non-English characters',
}) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const allOff = Object.fromEntries(Object.keys(defaultFilters).map((k) => [k, false]))
  const showNec =
    typeof hideNonEnglish === 'boolean' && typeof onHideNonEnglishChange === 'function'
  const onCount =
    items.filter((item) => Boolean(filters?.[item.key])).length + (showNec && hideNonEnglish ? 1 : 0)
  const totalCount = items.length + (showNec ? 1 : 0)
  const isDefault = items.every(
    (item) => Boolean(filters?.[item.key]) === Boolean(defaultFilters[item.key]),
  )
  const isCustom = !isDefault || (showNec && !hideNonEnglish)

  useEffect(() => {
    if (!open) return undefined
    function onDocMouseDown(event) {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false)
    }
    function onKey(event) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const btnClass =
    'rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:border-gray-300'

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onChange({ ...defaultFilters })}
        className={btnClass}
      >
        Default discovery
      </button>
      <button type="button" onClick={() => onChange(allOff)} className={btnClass}>
        All filters off
      </button>
      <div className="relative inline-flex" ref={rootRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={[
            'inline-flex items-center gap-1.5 rounded-lg border bg-white px-2.5 py-1.5 text-xs font-medium shadow-sm outline-none',
            'hover:border-gray-300 focus:border-[var(--encore-blue)] focus:ring-2 focus:ring-blue-100',
            isCustom ? 'border-blue-200 text-blue-800' : 'border-gray-200 text-gray-700',
          ].join(' ')}
          aria-expanded={open}
          aria-haspopup="menu"
        >
          Filters · {onCount} of {totalCount}
          <span className="text-gray-400">{open ? '▴' : '▾'}</span>
        </button>
        {open ? (
          <div className="absolute right-0 top-full z-40 mt-1 w-[min(18rem,calc(100vw-2rem))] rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg sm:right-auto sm:left-0">
            {items.map(({ key, label, title }) => (
              <label
                key={key}
                className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                title={title || 'Uncheck to disable this discovery filter'}
              >
                <input
                  type="checkbox"
                  checked={Boolean(filters[key])}
                  onChange={(event) =>
                    onChange({ ...filters, [key]: event.target.checked })
                  }
                  className="mt-0.5 rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
                />
                <span>{label}</span>
              </label>
            ))}
            {showNec ? (
              <label
                className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
                title={hideNonEnglishTitle}
              >
                <input
                  type="checkbox"
                  checked={hideNonEnglish}
                  onChange={(event) => onHideNonEnglishChange(event.target.checked)}
                  className="mt-0.5 rounded border-gray-300 text-[var(--encore-blue)] focus:ring-blue-100"
                />
                <span>Hide non-English</span>
              </label>
            ) : null}
          </div>
        ) : null}
      </div>
      {summary ? <span className="text-xs text-gray-500">{summary}</span> : null}
    </div>
  )
}
